import 'instant.page';
import axios from 'axios';
import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';
import focus from '@alpinejs/focus';
import morph from '@alpinejs/morph';
import persist from '@alpinejs/persist';

// Alpine components — small, registered eagerly so they fire when used
import navigation from './navigation.js';
import videoplayer from './videoplayer.js';
import reviews from './components/Alpine/google-reviews.js';
import stackCards from './components/stacked-cards.js';
import cart from './components/Alpine/cart.js';
import cartItem from './components/Alpine/cart-item.js';
import clusterNav from './components/Alpine/cluster-nav.js';
import map from './mapbox';

window.axios = axios;
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
window.Alpine = Alpine;

// --- Conditional / lazy imports based on DOM markers ---
// Tasks that must finish BEFORE Alpine.start() (e.g. Alpine component registration)
const blockingTasks = [];
// Fire-and-forget tasks (visual enhancements, telemetry, etc.)

if (document.querySelector('.splide')) {
    import('./splide');
}

if (document.querySelector('.glow-animation, .banner-icon, .image-container')) {
    import('./animations');
}

if (document.querySelector('[data-glossar-search]')) {
    // Blockierend, weil Alpine.data vor Alpine.start registriert sein muss. Der Import
    // laedt nur die Komponente; die Begriffsliste selbst holt sie erst beim Tippen.
    blockingTasks.push(
        import('./components/Alpine/glossar-search.js').then((m) => Alpine.data('glossarSearch', m.default)),
    );
}

if (document.querySelector('.scrollContainer')) {
    import('./scrollAnimation');
}

if (document.querySelector('.reference')) {
    import('./references');
}

if (document.querySelector('.masonry')) {
    import('./layeredboxes');
}

if (document.querySelector('.js-price-expand-toggle, .js-price-expand-button')) {
    import('./price-expand');
}

if (document.querySelector('.price-details, .price-content, .price-services')) {
    import('./price-equalHeight');
}

if (document.querySelector('altcha-widget, [data-altcha], form.altcha-form')) {
    // The <altcha-widget> renders a hidden input[name="altcha"], so its solution rides the
    // form's own FormData on submit — no header injection or Alpine magic needed.
    Promise.all([import('altcha'), import('altcha/i18n/de')]).then(() => {
        // Altcha 3.x dropped the per-widget `strings` attribute, so override the one custom
        // label ("Ich bin kein Bot" instead of the default "Ich bin kein Roboter") by merging
        // it into the shared German i18n store once both modules are registered.
        const i18n = window.$altcha?.i18n;

        i18n?.set('de', { ...i18n.get('de'), label: 'Ich bin kein Bot' });
    });
}

if (import.meta.env.PROD) {
    // Chunk-load failures are client-side noise (connection drops, ad blockers, tabs closed
    // mid-load, bots with partial JS engines) — the asset itself is present on the server.
    // Message wording differs per browser engine, hence three patterns.
    const chunkLoadFailure =
        /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;

    import('@flareapp/flare-client').then(({ flare }) => {
        flare.beforeEvaluate = (error) => (chunkLoadFailure.test(error.message) ? false : error);
        flare.light('XQFa3arDkndGh4kZj646CrZgrhCLsGJL');
    });
}

if (import.meta.env.DEV) {
    import('./linkcheck').then((m) => m.checkLinks());
}

// Sticky-Nav store
Alpine.store('stickyNav', {
    active: window.scrollY >= 50,
    init() {
        document.addEventListener('scroll', () => {
            this.active = window.scrollY >= 50;
        });
    },
});

Alpine.data('reviews', reviews);
Alpine.data('navigation', navigation);
Alpine.data('videoplayer', videoplayer);
Alpine.data('stackCards', stackCards);
Alpine.store('cart', cart);
Alpine.data('cart_item', cartItem);
Alpine.data('map', map);
Alpine.data('clusterNav', clusterNav);
Alpine.plugin([collapse, focus, morph, persist]);

Promise.allSettled(blockingTasks).then(() => Alpine.start());
